import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Alert,
  Typography,
  Paper,
  Container
} from '@mui/material';
import {
  Print as PrintIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { academicAPI, assessmentAPI, studentAPI, schoolAPI, reportCardAPI, gradingAPI, API_BASE_URL } from '../../services/api';
import HubFilters from './hub/HubFilters';
import ClassMarkListTable from './hub/ClassMarkListTable';
import StudentAllAssessmentsPdf from './hub/StudentAllAssessmentsPdf';
import { extractCollection, readErr } from './hub/hubUtils';

const ReportCardHub = () => {
  const currentYear = new Date().getFullYear();

  // Base data
  const [classes, setClasses] = useState([]);
  const [terms, setTerms] = useState([]);
  const [streams, setStreams] = useState([]);
  const [students, setStudents] = useState([]);
  const [assessmentConfigs, setAssessmentConfigs] = useState([]);
  const [school, setSchool] = useState(null);
  const [schoolLogo, setSchoolLogo] = useState(null); // base64 for PDF rendering
  const [gradingBoundaries, setGradingBoundaries] = useState([]);

  // Marks data
  const [studentMarks, setStudentMarks] = useState([]);

  // Loading & error
  const [loadingBase, setLoadingBase] = useState(true);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false); // eslint-disable-line no-unused-vars
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });

  // Filters
  const [filters, setFilters] = useState({
    academic_year: currentYear,
    term_id: '',
    class_id: '',
    class_stream_id: '',
    student_id: '',
    gender: '',
    status: '',
    assessment_type_ids: [],
    mark_status: '',
    grade_filter: '',
    min_percentage: '',
    max_percentage: '',
  });

  // Print preview state
  const [printMode, setPrintMode] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Convert logo to base64 via the backend asset proxy (avoids CORS on remote asset server)
  const fetchLogoAsBase64 = (url) => {
    if (!url) { setSchoolLogo(null); return; }
    const proxyUrl = `${API_BASE_URL}/asset-proxy?url=${encodeURIComponent(url)}`;
    fetch(proxyUrl)
      .then((r) => r.blob())
      .then((blob) => new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      }))
      .then((dataUrl) => setSchoolLogo(dataUrl))
      .catch(() => setSchoolLogo(url));
  };

  // Load base data
  const loadBaseData = useCallback(async () => {
    try {
      setLoadingBase(true);
      const [classRes, termRes, streamRes, studentRes, schoolRes] = await Promise.all([
        academicAPI.getAllClasses(),
        academicAPI.getTerms({ per_page: 1000 }),
        academicAPI.getClassStreamsForFilter(),
        studentAPI.getActive({ per_page: 10000 }),
        schoolAPI.get()
      ]);

      const termList = extractCollection(termRes);
      setClasses(extractCollection(classRes));
      setTerms(termList);
      setStreams(extractCollection(streamRes));
      setStudents(extractCollection(studentRes));

      const schoolData = schoolRes?.data || null;
      setSchool(schoolData);
      fetchLogoAsBase64(schoolData?.logo || null);

      // Auto-select current term
      const currentTerm = termList.find((t) => t.is_current);
      if (currentTerm) {
        setFilters((prev) => ({
          ...prev,
          term_id: currentTerm.id,
          academic_year: currentTerm.year || prev.academic_year,
        }));
      }
    } catch (err) {
      setSnack({ open: true, message: readErr(err, 'Failed to load base data'), severity: 'error' });
    } finally {
      setLoadingBase(false);
    }
  }, []);

  // Load marks + configs
  const loadMarksData = useCallback(async (currentFilters) => {
    if (!currentFilters.term_id) return;

    try {
      setLoading(true);

      const baseParams = {
        academic_year: currentFilters.academic_year,
        term_id: currentFilters.term_id,
        per_page: 1000,
      };
      if (currentFilters.class_id) baseParams.class_id = currentFilters.class_id;
      if (currentFilters.class_stream_id) baseParams.class_stream_id = currentFilters.class_stream_id;

      const rcParams = { ...baseParams, per_page: 10000 };
      const typeIds = currentFilters.assessment_type_ids || [];
      if (typeIds.length) rcParams.assessment_type_ids = typeIds.join(',');

      const gradingScalePromise = currentFilters.class_id
        ? gradingAPI.getClassGradingScaleForClass(currentFilters.class_id, { academic_year: currentFilters.academic_year })
            .then((r) => r?.data?.grading_scale?.grade_boundaries ?? [])
            .catch(() => [])
        : Promise.resolve([]);

      const [configRes, reportCardsRes, boundaries] = await Promise.all([
        assessmentAPI.getAssessmentConfigurations(baseParams),
        reportCardAPI.getReportCards(rcParams),
        gradingScalePromise,
      ]);

      setGradingBoundaries(Array.isArray(boundaries) ? boundaries : []);
      setAssessmentConfigs(extractCollection(configRes));
      let cards = extractCollection(reportCardsRes);

      if (typeIds.length) {
        const typeIdSet = new Set(typeIds.map(String));
        cards = cards.map((rc) => ({
          ...rc,
          subjects_data: (rc.subjects_data || []).map((sd) => ({
            ...sd,
            papers: (sd.papers || []).map((paper) => ({
              ...paper,
              assessments: (paper.assessments || []).filter(
                (a) => typeIdSet.has(String(a.assessment_type?.id))
              ),
            })).filter((paper) => paper.assessments.length > 0),
          })),
        }));
      }

      setStudentMarks(cards);
      setSnack({ open: true, message: `Loaded ${cards.length} students`, severity: 'success' });
    } catch (err) {
      setSnack({ open: true, message: readErr(err, 'Failed to load data'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBaseData(); }, [loadBaseData]);

  useEffect(() => {
    setStudentMarks([]);
    loadMarksData(filters);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.academic_year, filters.term_id, filters.class_id, filters.class_stream_id, filters.assessment_type_ids]);

  const handleRefresh  = () => loadBaseData();
  const handleLoadMarks = () => loadMarksData(filters);

  const handlePrintStudent = (student) => {
    setSelectedStudent(student);
    setPrintMode('student-all');
  };

  const handleCloseSnack = () => setSnack((p) => ({ ...p, open: false }));

  // Merge base64 logo into school object for PDFs
  const schoolForPdf = school ? { ...school, logo: schoolLogo } : null;

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        <Typography variant="h4" fontWeight={700} mb={1}>
          Assessment Report Hub
        </Typography>
        <Typography variant="body2" color="textSecondary" mb={3}>
          Export class mark lists and per-student assessment PDFs with aggregates, divisions, and school header/footer.
        </Typography>

        {/* Student PDF Dialog */}
        {printMode === 'student-all' && selectedStudent && (
          <Dialog
            open
            onClose={() => { setPrintMode(null); setSelectedStudent(null); }}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
              {selectedStudent.full_name || `${selectedStudent.last_name || ''} ${selectedStudent.first_name || ''}`.trim()} — Assessment Report
              <IconButton size="small" onClick={() => { setPrintMode(null); setSelectedStudent(null); }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 2 }}>
              <StudentAllAssessmentsPdf
                student={selectedStudent}
                studentMarks={studentMarks.filter((m) => String(m.student_id) === String(selectedStudent.id))}
                assessmentConfigs={assessmentConfigs}
                filters={filters}
                classes={classes}
                terms={terms}
                school={schoolForPdf}
                gradingBoundaries={gradingBoundaries}
              />
            </DialogContent>
          </Dialog>
        )}

        {/* Filters */}
        <HubFilters
          filters={filters}
          setFilters={setFilters}
          classes={classes}
          terms={terms}
          streams={streams}
          students={students}
          assessmentConfigs={assessmentConfigs}
          loadingBase={loadingBase}
          loading={loading}
          processing={processing}
          onLoad={handleLoadMarks}
          onRefresh={handleRefresh}
        />

        {/* Action Buttons */}
        <Box display="flex" gap={1} mb={3}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            disabled={loading || !studentMarks.length}
            onClick={() => setPrintMode('student-all')}
          >
            Print Preview
          </Button>
        </Box>

        {/* Loading indicator */}
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {/* Marks table */}
        {!loading && studentMarks.length > 0 && (
          <ClassMarkListTable
            studentMarks={studentMarks}
            assessmentConfigs={assessmentConfigs}
            students={students}
            filters={filters}
            classes={classes}
            terms={terms}
            school={schoolForPdf}
            gradingBoundaries={gradingBoundaries}
            onPrintStudent={handlePrintStudent}
          />
        )}

        {!loading && studentMarks.length === 0 && !printMode && (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="textSecondary">
              {filters.term_id ? 'No marks found for the selected filters.' : 'Select a term to view marks.'}
            </Typography>
          </Paper>
        )}

        {/* Snackbar */}
        <Snackbar
          open={snack.open}
          autoHideDuration={6000}
          onClose={handleCloseSnack}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnack} severity={snack.severity}>
            {snack.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default ReportCardHub;

